import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchReportitemproductioncostings,
    createReportitemproductioncosting,
    updateReportitemproductioncosting,
    deleteReportitemproductioncosting,
    downloadReportitemproductioncosting,
    setPagination,
    setSort,
    searchReportitemproductioncostings,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination,

    changeClassicFilter
} from '../../store/modules/reportitemproductioncosting/reportitemproductioncostingActions';
import ReportitemproductioncostingForm from '../../components/pageModule/Reportitemproductioncostings/ReportitemproductioncostingForm';

const MySwal = withReactContent(Swal);

const Reportitemproductioncostings = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = []
}) => {

    const reportitemproductioncostingState = useSelector(state => state.reportitemproductioncosting);
    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createReportitemproductioncosting(formData));
            toast.success('Reportitemproductioncosting created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating reportitemproductioncosting', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateReportitemproductioncosting(id, formData));
            toast.success('Reportitemproductioncosting updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating reportitemproductioncosting', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteReportitemproductioncosting(id));
                toast.success('Reportitemproductioncosting deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting reportitemproductioncosting', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handleDownload = async (type) => {
        try {
            const response = await dispatch(downloadReportitemproductioncosting(type));
            window.open(response.url, '_blank')
            return response;
        } catch (error) {
            toast.error(error.message || 'Error downloading reportitemproductioncostings', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name ?? '-'
        },
        {
            name: 'Code',
            selector: row => row.item_code,
        },
        {
            name: 'Quantity',
            selector: row => row.total_order_quantity,
        },
        {
            name: 'Order Value',
            selector: row => row.total_order_amount,
        },
        {
            name: 'RM Cost',
            selector: row => row.total_material_cost,
        },
        {
            name: 'Labour Cost',
            selector: row => row.total_labour_cost,
        },
        {
            name: 'Total Cost',
            selector: row => row.total_produced_total,
        }
    ];

    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchReportitemproductioncostings(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        download: handleDownload,
        search: (search) => dispatch(searchReportitemproductioncostings(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),

        onClassicFilterChange: (name, value) => dispatch(changeClassicFilter(name, value)),
    };

    const defaultAvailableActions = ['export_pdf', 'export_excel'];
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Report item wise production costing"
                moduleName="Reportitemproductioncosting"
                moduleKey="reportitemproductioncostings"
                state={reportitemproductioncostingState}
                actions={actions}
                FormComponent={ReportitemproductioncostingForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                selectableRows={false}
                onSelectedRowsChange={onSelectedRowsChange}
                onClickedRow={onClickedRow}
                selectedIds={selectedIds}
                externalCondition={externalCondition}
            />
        </>
    );
};

export default Reportitemproductioncostings; 